import { DestroyRef, inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DialogService, NavigationService } from '@sol/components';
import { EmbedService } from '@sol/components';
import { AsmRouteId } from '@asm/components-core/models/asm-route-id';
import { filter } from 'rxjs/operators';

// ─── Contract message shapes (from host-bridge-contract.md) ──────────────────

// The Vue -> Angular embed is platform-only by design. Scope and subscope are
// the only host-owned context that crosses this boundary; partner and admin
// pages are never embedded. The named scope/subscope fields in this contract
// reflect that constraint and should not be generalized speculatively.

export type SequenceId = number;

export interface HostInitMessage {
  type: 'host:init';
  sequenceId: SequenceId;
  payload: {
    childPath: string;
    /** Angular-owned query params from the deep-link URL to restore on first load. Does NOT include scope/subscope. */
    childQuery: Record<string, string>;
    scope: string | undefined;
    subscope: string | undefined;
    parentOrigin: string;
  };
}

export interface HostSetLocationMessage {
  type: 'host:set-location';
  sequenceId: SequenceId;
  payload: {
    childPath: string;
    childQuery: Record<string, string>;
    scope: string | undefined;
    subscope: string | undefined;
  };
}

export interface HostNavigateMessage {
  type: 'host:navigate';
  sequenceId: SequenceId;
  payload: {
    childPath: string;
    childQuery: Record<string, string>;
  };
}

export type HostMessage = HostInitMessage | HostSetLocationMessage | HostNavigateMessage;

export interface ChildReadyMessage {
  type: 'child:ready';
  sequenceId: SequenceId;
  payload: Record<string, never>;
}

export interface ChildLocationChangedMessage {
  type: 'child:location-changed';
  sequenceId: SequenceId;
  echoedSequenceId: SequenceId | null;
  payload: {
    childPath: string;
    childQuery: Record<string, string>;
  };
}

export interface ChildDialogOpenedMessage {
  type: 'child:dialog-opened';
  sequenceId: SequenceId;
  payload: Record<string, never>;
}

export interface ChildDialogClosedMessage {
  type: 'child:dialog-closed';
  sequenceId: SequenceId;
  payload: Record<string, never>;
}

export interface ChildNavigateHostMessage {
  type: 'child:navigate-host';
  sequenceId: SequenceId;
  payload: {
    hostPath: string;
    hostQuery?: Record<string, string>;
  };
}

export type ChildMessage =
  | ChildReadyMessage
  | ChildLocationChangedMessage
  | ChildDialogOpenedMessage
  | ChildDialogClosedMessage
  | ChildNavigateHostMessage;

// ─── Service ─────────────────────────────────────────────────────────────────

/**
 * Transport service for the asm-platform ↔ Vue host iframe bridge.
 * Owns the inbound message listener, the pinned parent origin, and outbound
 * postMessage emissions for navigation/dialog state. Whether the app is
 * embedded is read from `BrowserTools.isEmbedded()` — not maintained here.
 */
@Injectable({
  providedIn: 'root',
})
export class HostBridgeService {
  // Dependencies
  private readonly router = inject(Router);
  private readonly dialogService = inject(DialogService);
  private readonly embedService = inject(EmbedService);
  private readonly navigationService = inject(NavigationService);
  private readonly destroyRef = inject(DestroyRef);

  // Origin pinned from the first inbound host:init message.
  private pinnedParentOrigin: string | undefined = undefined;

  // Sequence counter for outbound child messages.
  private _nextSeqId = 1;

  /**
   * When non-null, the next child:location-changed will echo this ID back to
   * the host, indicating the navigation was host-driven (not organic).
   */
  private _pendingEchoSeqId: SequenceId | null = null;

  /**
   * Flipped to true by setup() after sessionService.fetch() resolves.
   * Outbound subscriptions are wired in the constructor (injection context)
   * but suppress emissions until this flag is true.
   */
  private _ready = false;

  /** Guards setup() against double-registration of the inbound message listener. */
  private _inboundAttached = false;

  /**
   * Stable bound reference to onMessage — stored so removeEventListener can
   * remove the exact same function object that addEventListener received.
   */
  private readonly _onMessage = (ev: MessageEvent) => this.onMessage(ev);

  constructor() {
    // Wire outbound subscriptions here so takeUntilDestroyed() runs in the
    // injection context established by the constructor. Emissions are gated
    // on _ready so nothing is sent before setup() is called.
    this.setupOutbound();
    // Register inbound listener cleanup unconditionally in the injection
    // context so DestroyRef.onDestroy fires when the service is destroyed.
    this.destroyRef.onDestroy(() => window.removeEventListener('message', this._onMessage));
  }

  /**
   * Called once from AppComponent after session is fetched and the app is ready.
   * Sets up all inbound bridge wiring, flips the outbound-ready flag, then sends
   * child:ready so Vue can start the handshake (Vue will respond with host:init).
   * Idempotent: subsequent calls are no-ops (guarded by _inboundAttached).
   */
  public setup(): void {
    if (this._inboundAttached) {
      return;
    }
    this.setupInbound();
    this._ready = true;
    this.postToParent({ type: 'child:ready', sequenceId: this.nextSeqId(), payload: {} });
  }

  // ─── Private: inbound (host → child) ─────────────────────────────────────────

  private setupInbound(): void {
    this._inboundAttached = true;
    window.addEventListener('message', this._onMessage);
  }

  private onMessage(event: MessageEvent): void {
    if (!this.isHostMessage(event.data)) {
      return;
    }

    const msg = event.data as HostMessage;

    if (this.pinnedParentOrigin === undefined) {
      // Handshake not yet complete. Only host:init is allowed before the origin
      // is pinned. Any other message type arriving before host:init could be a
      // postMessage injection from an untrusted origin — reject it.
      if (msg.type !== 'host:init') {
        return;
      }
      // Validate that the envelope origin matches the parentOrigin the sender
      // claims in the payload — both must agree before we pin.
      if (event.origin !== msg.payload.parentOrigin) {
        return;
      }
      this.pinnedParentOrigin = msg.payload.parentOrigin;
    } else {
      // Handshake complete — all subsequent messages must come from the pinned origin.
      if (event.origin !== this.pinnedParentOrigin) {
        return;
      }
    }

    switch (msg.type) {
      case 'host:init':
        this.handleHostInit(msg);
        break;
      case 'host:set-location':
        this.handleHostSetLocation(msg);
        break;
      case 'host:navigate':
        this.handleHostNavigate(msg);
        break;
    }
  }

  private handleHostInit(msg: HostInitMessage): void {
    const { childPath, childQuery, scope, subscope, parentOrigin } = msg.payload;
    this._pendingEchoSeqId = msg.sequenceId;
    // Pin the trusted parent origin on EmbedService so navigateHost can target it.
    this.embedService.setParentOrigin(parentOrigin);
    this.navigateToChildPath(childPath, HostBridgeService.mergeHostScope(childQuery, scope, subscope));
  }

  private handleHostSetLocation(msg: HostSetLocationMessage): void {
    const { childPath, childQuery, scope, subscope } = msg.payload;
    this._pendingEchoSeqId = msg.sequenceId;
    this.navigateToChildPath(childPath, HostBridgeService.mergeHostScope(childQuery, scope, subscope));
  }

  private handleHostNavigate(msg: HostNavigateMessage): void {
    const { childPath, childQuery } = msg.payload;
    this._pendingEchoSeqId = msg.sequenceId;
    // host:navigate deliberately carries only Angular-owned query state.
    this.navigateToChildPath(childPath, childQuery);
  }

  /**
   * Navigate Angular router to a child path received from the host.
   * child paths look like "/atp/exposed-accounts/123/overview".
   * Uses NavigationService.navigateByPath so downstream NavigationState stays in sync.
   */
  private navigateToChildPath(childPath: string, childQuery: Record<string, string>): void {
    this.navigationService.navigateByPath(childPath, childQuery).catch(err => {
      console.error('Host-driven navigation failed', { childPath, childQuery, err });
    });
  }

  // ─── Private: outbound (child → host) ────────────────────────────────────────

  /**
   * Subscribe to router NavigationEnd events and dialog state changes to emit
   * the corresponding child messages. Called from the constructor so that
   * takeUntilDestroyed() runs inside the injection context. Emissions are
   * suppressed until _ready is true (set by setup()).
   */
  private setupOutbound(): void {
    // Emit child:location-changed on every NavigationEnd so Vue URL stays in sync.
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(event => {
        const navEnd = event as NavigationEnd;
        const childPath = HostBridgeService.extractChildPath(navEnd.urlAfterRedirects);
        if (childPath === '/' + AsmRouteId.error) {
          return;
        }
        const childQuery = HostBridgeService.extractChildQuery(navEnd.urlAfterRedirects);
        this.emitLocationChanged(childPath, childQuery);
      });

    // Emit child:dialog-opened / child:dialog-closed when dialog state changes.
    this.dialogService.watchComputed(
      state => state.entries.length > 0,
      hasDialogs => {
        if (hasDialogs) {
          this.emitDialogOpened();
        } else {
          this.emitDialogClosed();
        }
      },
    );
  }

  private emitLocationChanged(childPath: string, childQuery: Record<string, string>): void {
    if (!this._ready) {
      return;
    }
    const echoedSequenceId = this._pendingEchoSeqId;
    this._pendingEchoSeqId = null;
    const msg: ChildLocationChangedMessage = {
      type: 'child:location-changed',
      sequenceId: this.nextSeqId(),
      echoedSequenceId,
      payload: { childPath, childQuery },
    };
    this.postToParent(msg);
  }

  private emitDialogOpened(): void {
    if (!this._ready) {
      return;
    }
    const msg: ChildDialogOpenedMessage = {
      type: 'child:dialog-opened',
      sequenceId: this.nextSeqId(),
      payload: {},
    };
    this.postToParent(msg);
  }

  private emitDialogClosed(): void {
    if (!this._ready) {
      return;
    }
    const msg: ChildDialogClosedMessage = {
      type: 'child:dialog-closed',
      sequenceId: this.nextSeqId(),
      payload: {},
    };
    this.postToParent(msg);
  }

  private postToParent(msg: ChildMessage): void {
    // child:ready is the bootstrap message sent before host:init arrives.
    // At this point the parent origin is not yet pinned, so we use the document
    // referrer as a best-effort origin guard. If the referrer is unavailable
    // we fall back to '*' — child:ready carries no sensitive data.
    if (msg.type === 'child:ready') {
      const targetOrigin = document.referrer ? new URL(document.referrer).origin : '*';
      window.parent.postMessage(msg, targetOrigin);
      return;
    }

    // All subsequent messages require a pinned origin (established by host:init).
    if (!this.pinnedParentOrigin) {
      return;
    }
    window.parent.postMessage(msg, this.pinnedParentOrigin);
  }

  private nextSeqId(): SequenceId {
    return this._nextSeqId++;
  }

  private isHostMessage(data: unknown): data is HostMessage {
    return (
      typeof data === 'object' &&
      data !== null &&
      typeof (data as any).type === 'string' &&
      ((data as any).type === 'host:init' ||
        (data as any).type === 'host:set-location' ||
        (data as any).type === 'host:navigate')
    );
  }

  private static mergeHostScope(
    childQuery: Record<string, string>,
    scope: string | undefined,
    subscope: string | undefined,
  ): Record<string, string> {
    const merged: Record<string, string> = { ...childQuery };
    if (scope) {
      merged['scope'] = scope;
    }
    if (subscope) {
      merged['subscope'] = subscope;
    }
    return merged;
  }

  /**
   * Extract the child path (path only, no query string) from an Angular URL.
   * e.g. "/atp/exposed-accounts?filter=foo" → "/atp/exposed-accounts"
   */
  private static extractChildPath(url: string): string {
    const [path] = url.split('?');
    return path || '/';
  }

  /**
   * Extract Angular-owned query params from a URL, stripping scope and subscope
   * (those are Vue-owned and must not appear in child:location-changed payload).
   */
  private static extractChildQuery(url: string): Record<string, string> {
    const queryStart = url.indexOf('?');
    if (queryStart === -1) {
      return {};
    }
    const params = new URLSearchParams(url.slice(queryStart + 1));
    params.delete('scope');
    params.delete('subscope');
    const result: Record<string, string> = {};
    params.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }
}
